import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { 
  Elements, 
  CardElement, 
  PaymentRequestButtonElement,
  useStripe, 
  useElements 
} from '@stripe/react-stripe-js';
import { useCart } from '../../context/CartContext';
import API_URL from '../../config';

// Initialize Stripe from environment variable
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Stripe Card Element styling to match our dark theme
const cardElementOptions = {
  style: {
    base: {
      fontSize: '14px',
      color: '#F1F1F1',
      fontFamily: "'TASA Orbiter Display', sans-serif",
      '::placeholder': {
        color: '#6F6F6F',
      },
      backgroundColor: 'transparent',
    },
    invalid: {
      color: '#E63230',
      iconColor: '#E63230',
    },
  },
  hidePostalCode: true,
};

// Shipping cost logic
const EUROPE_COUNTRIES = ['Germany','France','Italy','Spain','Netherlands','Belgium','Austria','Switzerland','Portugal','Ireland','Sweden','Norway','Denmark','Finland','Poland','Czech Republic','Greece','Hungary','Romania','Bulgaria','Croatia','Slovakia','Slovenia','Lithuania','Latvia','Estonia','Luxembourg','Malta','Cyprus','Iceland','Serbia','Ukraine','Turkey'];
const ASIA_COUNTRIES = ['Japan','South Korea','China','India','Singapore','Hong Kong','Taiwan','Thailand','Malaysia','Indonesia','Philippines','Vietnam','United Arab Emirates','Saudi Arabia','Israel','Qatar','Kuwait'];
const SOUTH_AMERICA_COUNTRIES = ['Brazil','Argentina','Chile','Colombia','Peru','Uruguay'];

const getShippingCost = (country, total) => {
  if (total >= 50) return 0;
  if (country === 'United Kingdom') return 12;
  if (EUROPE_COUNTRIES.includes(country)) return 10;
  if (ASIA_COUNTRIES.includes(country)) return 15;
  if (SOUTH_AMERICA_COUNTRIES.includes(country)) return 15;
  return 15;
};

// Helper function to convert country name to ISO code
const getCountryCode = (countryName) => {
  const countryCodes = {
    // Europe
    'United Kingdom': 'GB',
    'Germany': 'DE',
    'France': 'FR',
    'Italy': 'IT',
    'Spain': 'ES',
    'Netherlands': 'NL',
    'Belgium': 'BE',
    'Austria': 'AT',
    'Switzerland': 'CH',
    'Portugal': 'PT',
    'Ireland': 'IE',
    'Sweden': 'SE',
    'Norway': 'NO',
    'Denmark': 'DK',
    'Finland': 'FI',
    'Poland': 'PL',
    'Czech Republic': 'CZ',
    'Greece': 'GR',
    'Hungary': 'HU',
    'Romania': 'RO',
    'Bulgaria': 'BG',
    'Croatia': 'HR',
    'Slovakia': 'SK',
    'Slovenia': 'SI',
    'Lithuania': 'LT',
    'Latvia': 'LV',
    'Estonia': 'EE',
    'Luxembourg': 'LU',
    'Malta': 'MT',
    'Cyprus': 'CY',
    'Iceland': 'IS',
    'Serbia': 'RS',
    'Ukraine': 'UA',
    'Turkey': 'TR',
    // North America
    'United States': 'US',
    'Canada': 'CA',
    'Mexico': 'MX',
    // South America
    'Brazil': 'BR',
    'Argentina': 'AR',
    'Chile': 'CL',
    'Colombia': 'CO',
    'Peru': 'PE',
    'Uruguay': 'UY',
    // Asia
    'Japan': 'JP',
    'South Korea': 'KR',
    'China': 'CN',
    'India': 'IN',
    'Singapore': 'SG',
    'Hong Kong': 'HK',
    'Taiwan': 'TW',
    'Thailand': 'TH',
    'Malaysia': 'MY',
    'Indonesia': 'ID',
    'Philippines': 'PH',
    'Vietnam': 'VN',
    'United Arab Emirates': 'AE',
    'Saudi Arabia': 'SA',
    'Israel': 'IL',
    'Qatar': 'QA',
    'Kuwait': 'KW',
    // Oceania
    'Australia': 'AU',
    'New Zealand': 'NZ',
    // Africa
    'South Africa': 'ZA',
    'Egypt': 'EG',
    'Morocco': 'MA',
    'Nigeria': 'NG',
    'Kenya': 'KE',
  };
  return countryCodes[countryName] || countryName;
};

// The actual checkout form component
const CheckoutForm = () => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const { cartItems, cartTotal, clearCart } = useCart();
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [canMakePayment, setCanMakePayment] = useState(false);

  const [formData, setFormData] = useState({
    email: '',
    emailOffers: true,
    country: 'United Kingdom',
    firstName: '',
    lastName: '',
    address: '',
    apartment: '',
    city: '',
    postcode: '',
    phone: '',
    useShippingAsBilling: true,
  });

  const shippingCost = getShippingCost(formData.country, cartTotal);
  const orderTotal = cartTotal + shippingCost;

  // Helper to format cart items for the API
  const formatItemsForAPI = () => {
    return cartItems.map(item => ({
      id: item.id,
      name: item.name,
      size: item.size,
      quantity: item.quantity,
      price: item.price,
      image: item.image || '',
    }));
  };

  // Inject responsive styles
  useEffect(() => {
    const styleId = 'checkout-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        .checkout-main {
          padding-top: 70px;
          display: flex;
          min-height: calc(100vh - 70px);
        }
        
        .checkout-form-section {
          width: 50%;
          padding: 40px 60px;
          border-right: 1px solid #1C1C1C;
          overflow-y: auto;
        }
        
        .checkout-order-section {
          width: 50%;
          padding: 40px 60px;
          background-color: #0A0A0A;
        }
        
        @media (max-width: 768px) {
          .checkout-main {
            flex-direction: column-reverse;
          }
          
          .checkout-form-section {
            width: 100%;
            padding: 24px 16px;
            border-right: none;
          }
          
          .checkout-order-section {
            width: 100%;
            padding: 24px 16px;
          }
        }
        
        /* Dark theme for all form inputs */
        .checkout-input,
        .checkout-select {
          width: 100%;
          padding: 14px 16px;
          background-color: #1C1C1C !important;
          border: 1px solid #333;
          border-radius: 4px;
          color: #F1F1F1 !important;
          font-size: 14px;
          font-family: 'TASA Orbiter Display', sans-serif;
          outline: none;
          transition: border-color 0.2s ease;
          box-sizing: border-box;
        }
        
        .checkout-input::placeholder {
          color: #6F6F6F;
        }
        
        .checkout-input:focus,
        .checkout-select:focus {
          border-color: #F1F1F1;
        }
        
        .checkout-input:-webkit-autofill,
        .checkout-input:-webkit-autofill:hover,
        .checkout-input:-webkit-autofill:focus,
        .checkout-select:-webkit-autofill,
        .checkout-select:-webkit-autofill:hover,
        .checkout-select:-webkit-autofill:focus {
          -webkit-text-fill-color: #F1F1F1 !important;
          -webkit-box-shadow: 0 0 0px 1000px #1C1C1C inset !important;
          box-shadow: 0 0 0px 1000px #1C1C1C inset !important;
          background-color: #1C1C1C !important;
          border: 1px solid #333 !important;
          transition: background-color 5000s ease-in-out 0s;
        }
        
        /* Dark theme for select dropdown */
        .checkout-select {
          cursor: pointer;
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%238A8A8A' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 16px center;
        }
        
        .checkout-select option {
          background-color: #1C1C1C;
          color: #F1F1F1;
          padding: 12px;
        }
        
        .checkout-select option:hover,
        .checkout-select option:focus,
        .checkout-select option:checked {
          background-color: #333;
          color: #F1F1F1;
        }
        
        /* Custom checkbox styling */
        .checkout-checkbox {
          width: 18px;
          height: 18px;
          accent-color: #F1F1F1;
          cursor: pointer;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // Initialize Stripe Payment Request (Apple Pay / Google Pay)
  useEffect(() => {
    if (!stripe || !cartTotal) return;

    const pr = stripe.paymentRequest({
      country: 'GB',
      currency: 'usd',
      total: {
        label: 'Order Total',
        amount: Math.round(orderTotal * 100),
      },
      requestPayerName: true,
      requestPayerEmail: true,
      requestShipping: true,
      shippingOptions: [
        {
          id: 'standard',
          label: 'Standard Shipping',
          detail: '5-10 business days',
          amount: 0,
        },
      ],
    });

    // Check if Payment Request is available (Apple Pay / Google Pay)
    pr.canMakePayment().then(result => {
      if (result) {
        setPaymentRequest(pr);
        setCanMakePayment(true);
      }
    });

    // Handle payment method submission
    pr.on('paymentmethod', async (ev) => {
      try {
        const response = await fetch(`${API_URL}/api/create-payment-intent`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            amount: Math.round(orderTotal * 100),
            currency: 'usd',
            email: ev.payerEmail,
            shipping: ev.shippingAddress ? {
              name: ev.shippingAddress.recipient,
              address: {
                line1: ev.shippingAddress.addressLine[0],
                line2: ev.shippingAddress.addressLine[1] || '',
                city: ev.shippingAddress.city,
                postal_code: ev.shippingAddress.postalCode,
                country: ev.shippingAddress.country,
              },
            } : undefined,
            items: formatItemsForAPI(),
          }),
        });

        const { clientSecret, error: backendError } = await response.json();

        if (backendError) {
          ev.complete('fail');
          return;
        }

        const { error, paymentIntent } = await stripe.confirmCardPayment(
          clientSecret,
          { payment_method: ev.paymentMethod.id },
          { handleActions: false }
        );

        if (error) {
          ev.complete('fail');
          return;
        }

        ev.complete('success');

        if (paymentIntent.status === 'succeeded') {
          clearCart();
          navigate('/order-confirmation', {
            state: {
              orderId: paymentIntent.id,
              email: ev.payerEmail,
            }
          });
        }
      } catch (err) {
        ev.complete('fail');
      }
    });
  }, [stripe, cartTotal, clearCart, navigate]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setPaymentError(null);

    try {
      const response = await fetch(`${API_URL}/api/create-payment-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: Math.round(orderTotal * 100),
          currency: 'usd',
          email: formData.email,
          shipping: {
            name: `${formData.firstName} ${formData.lastName}`,
            address: {
              line1: formData.address,
              line2: formData.apartment,
              city: formData.city,
              postal_code: formData.postcode,
              country: getCountryCode(formData.country),
            },
          },
          items: formatItemsForAPI(),
        }),
      });

      const { clientSecret, error: backendError } = await response.json();

      if (backendError) {
        throw new Error(backendError);
      }

      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: {
            name: `${formData.firstName} ${formData.lastName}`,
            email: formData.email,
            phone: formData.phone,
            address: formData.useShippingAsBilling ? {
              line1: formData.address,
              line2: formData.apartment,
              city: formData.city,
              postal_code: formData.postcode,
              country: getCountryCode(formData.country),
            } : undefined,
          },
        },
      });

      if (error) {
        throw new Error(error.message);
      }

      if (paymentIntent.status === 'succeeded') {
        // Send order details for confirmation emails
        try {
          await fetch(`${API_URL}/api/order-complete`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              orderId: paymentIntent.id,
              email: formData.email,
              name: `${formData.firstName} ${formData.lastName}`,
              address: {
                line1: formData.address,
                line2: formData.apartment,
                city: formData.city,
                postal_code: formData.postcode,
                country: formData.country,
              },
              items: cartItems,
              total: orderTotal,
            }),
          });
        } catch (emailError) {
          console.error('Error sending confirmation emails:', emailError);
        }

        clearCart();
        navigate('/order-confirmation', { 
          state: { 
            orderId: paymentIntent.id,
            email: formData.email,
          } 
        });
      }
    } catch (err) {
      setPaymentError(err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const sectionTitleStyle = {
    fontSize: '14px',
    color: '#8A8A8A',
    marginBottom: '16px',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  };

  if (cartItems.length === 0) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#0D0D0D',
        fontFamily: "'TASA Orbiter Display', sans-serif",
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#F1F1F1',
      }}>
        <p style={{ marginBottom: '24px', color: '#8A8A8A' }}>Your cart is empty</p>
        <Link
          to="/collection"
          style={{
            padding: '14px 32px',
            backgroundColor: '#F1F1F1',
            color: '#0D0D0D',
            textDecoration: 'none',
            fontSize: '14px',
          }}
        >
          Continue Shopping
        </Link>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#0D0D0D',
      fontFamily: "'TASA Orbiter Display', sans-serif",
    }}>
      <Helmet>
        <title>Checkout | RTK</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      {/* Header */}
      <header style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        height: '70px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#0D0D0D',
        borderBottom: '1px solid #1C1C1C',
      }}>
        <Link to="/">
          <img
            src="/logo.png"
            alt="Logo"
            style={{ height: '40px', width: 'auto' }}
          />
        </Link>
      </header>

      {/* Main Content - 50/50 Split on desktop, stacked on mobile */}
      <main className="checkout-main">
        {/* Left Side - Customer Details */}
        <div className="checkout-form-section">
          <form onSubmit={handleSubmit}>
            {/* Express Checkout - Only shows if Google Pay/Apple Pay available */}
            {canMakePayment && paymentRequest && (
              <div style={{ marginBottom: '40px' }}>
                <p style={{
                  ...sectionTitleStyle,
                  textAlign: 'center',
                }}>
                  Express checkout
                </p>
                
                <div style={{ height: '44px' }}>
                  <PaymentRequestButtonElement
                    options={{
                      paymentRequest,
                      style: {
                        paymentRequestButton: {
                          type: 'default',
                          theme: 'light',
                          height: '44px',
                        },
                      },
                    }}
                  />
                </div>

                {/* OR Divider */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginTop: '40px',
                }}>
                  <div style={{ flex: 1, height: '1px', backgroundColor: '#333' }} />
                  <span style={{ padding: '0 16px', color: '#6F6F6F', fontSize: '13px' }}>OR</span>
                  <div style={{ flex: 1, height: '1px', backgroundColor: '#333' }} />
                </div>
              </div>
            )}

            {/* Contact */}
            <div style={{ marginBottom: '32px' }}>
              <p style={sectionTitleStyle}>Contact</p>
              <input
                type="email"
                name="email"
                placeholder="Email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="checkout-input"
              />
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                marginTop: '12px',
                fontSize: '13px',
                color: '#8A8A8A',
                cursor: 'pointer',
              }}>
                <input
                  type="checkbox"
                  name="emailOffers"
                  checked={formData.emailOffers}
                  onChange={handleInputChange}
                  className="checkout-checkbox"
                />
                Email me with news and offers
              </label>
            </div>

            {/* Delivery */}
            <div style={{ marginBottom: '32px' }}>
              <p style={sectionTitleStyle}>Delivery</p>
              
              {/* Country */}
              <div style={{ marginBottom: '12px' }}>
                <select
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  className="checkout-select"
                >
                  {/* Europe */}
                  <option value="United Kingdom">United Kingdom</option>
                  <option value="Germany">Germany</option>
                  <option value="France">France</option>
                  <option value="Italy">Italy</option>
                  <option value="Spain">Spain</option>
                  <option value="Netherlands">Netherlands</option>
                  <option value="Belgium">Belgium</option>
                  <option value="Austria">Austria</option>
                  <option value="Switzerland">Switzerland</option>
                  <option value="Portugal">Portugal</option>
                  <option value="Ireland">Ireland</option>
                  <option value="Sweden">Sweden</option>
                  <option value="Norway">Norway</option>
                  <option value="Denmark">Denmark</option>
                  <option value="Finland">Finland</option>
                  <option value="Poland">Poland</option>
                  <option value="Czech Republic">Czech Republic</option>
                  <option value="Greece">Greece</option>
                  <option value="Hungary">Hungary</option>
                  <option value="Romania">Romania</option>
                  <option value="Bulgaria">Bulgaria</option>
                  <option value="Croatia">Croatia</option>
                  <option value="Slovakia">Slovakia</option>
                  <option value="Slovenia">Slovenia</option>
                  <option value="Lithuania">Lithuania</option>
                  <option value="Latvia">Latvia</option>
                  <option value="Estonia">Estonia</option>
                  <option value="Luxembourg">Luxembourg</option>
                  <option value="Malta">Malta</option>
                  <option value="Cyprus">Cyprus</option>
                  <option value="Iceland">Iceland</option>
                  <option value="Serbia">Serbia</option>
                  <option value="Ukraine">Ukraine</option>
                  <option value="Turkey">Turkey</option>
                  
                  {/* North America */}
                  <option value="United States">United States</option>
                  <option value="Canada">Canada</option>
                  <option value="Mexico">Mexico</option>
                  
                  {/* South America */}
                  <option value="Brazil">Brazil</option>
                  <option value="Argentina">Argentina</option>
                  <option value="Chile">Chile</option>
                  <option value="Colombia">Colombia</option>
                  <option value="Peru">Peru</option>
                  <option value="Uruguay">Uruguay</option>
                  
                  {/* Asia */}
                  <option value="Japan">Japan</option>
                  <option value="South Korea">South Korea</option>
                  <option value="China">China</option>
                  <option value="India">India</option>
                  <option value="Singapore">Singapore</option>
                  <option value="Hong Kong">Hong Kong</option>
                  <option value="Taiwan">Taiwan</option>
                  <option value="Thailand">Thailand</option>
                  <option value="Malaysia">Malaysia</option>
                  <option value="Indonesia">Indonesia</option>
                  <option value="Philippines">Philippines</option>
                  <option value="Vietnam">Vietnam</option>
                  <option value="United Arab Emirates">United Arab Emirates</option>
                  <option value="Saudi Arabia">Saudi Arabia</option>
                  <option value="Israel">Israel</option>
                  <option value="Qatar">Qatar</option>
                  <option value="Kuwait">Kuwait</option>
                  
                  {/* Oceania */}
                  <option value="Australia">Australia</option>
                  <option value="New Zealand">New Zealand</option>
                  
                  {/* Africa */}
                  <option value="South Africa">South Africa</option>
                  <option value="Egypt">Egypt</option>
                  <option value="Morocco">Morocco</option>
                  <option value="Nigeria">Nigeria</option>
                  <option value="Kenya">Kenya</option>
                </select>
              </div>

              {/* Name Row */}
              <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                <input
                  type="text"
                  name="firstName"
                  placeholder="First name (optional)"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className="checkout-input"
                  style={{ flex: 1 }}
                />
                <input
                  type="text"
                  name="lastName"
                  placeholder="Last name"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                  className="checkout-input"
                  style={{ flex: 1 }}
                />
              </div>

              {/* Address */}
              <input
                type="text"
                name="address"
                placeholder="Address"
                value={formData.address}
                onChange={handleInputChange}
                required
                className="checkout-input"
                style={{ marginBottom: '12px' }}
              />

              {/* Apartment */}
              <input
                type="text"
                name="apartment"
                placeholder="Apartment, suite, etc. (optional)"
                value={formData.apartment}
                onChange={handleInputChange}
                className="checkout-input"
                style={{ marginBottom: '12px' }}
              />

              {/* City & Postcode Row */}
              <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                <input
                  type="text"
                  name="city"
                  placeholder="City"
                  value={formData.city}
                  onChange={handleInputChange}
                  required
                  className="checkout-input"
                  style={{ flex: 1 }}
                />
                <input
                  type="text"
                  name="postcode"
                  placeholder="Postcode"
                  value={formData.postcode}
                  onChange={handleInputChange}
                  required
                  className="checkout-input"
                  style={{ flex: 1 }}
                />
              </div>

              {/* Phone */}
              <input
                type="tel"
                name="phone"
                placeholder="Phone (optional)"
                value={formData.phone}
                onChange={handleInputChange}
                className="checkout-input"
              />
            </div>

            {/* Payment */}
            <div style={{ marginBottom: '32px' }}>
              <p style={sectionTitleStyle}>Payment</p>
              <p style={{
                fontSize: '12px',
                color: '#6F6F6F',
                marginBottom: '16px',
              }}>
                All transactions are secure and encrypted.
              </p>

              {/* Stripe Card Element */}
              <div style={{
                border: '1px solid #333',
                borderRadius: '4px',
                overflow: 'hidden',
              }}>
                {/* Credit Card Header */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '16px',
                  backgroundColor: '#1C1C1C',
                  borderBottom: '1px solid #333',
                }}>
                  <span style={{ color: '#F1F1F1', fontSize: '14px' }}>Credit card</span>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <span style={{ padding: '4px 8px', backgroundColor: '#1A1F71', borderRadius: '4px', fontSize: '10px', color: '#FFF' }}>VISA</span>
                    <span style={{ padding: '4px 8px', backgroundColor: '#EB001B', borderRadius: '4px', fontSize: '10px', color: '#FFF' }}>MC</span>
                    <span style={{ padding: '4px 8px', backgroundColor: '#006FCF', borderRadius: '4px', fontSize: '10px', color: '#FFF' }}>AMEX</span>
                  </div>
                </div>

                {/* Stripe Card Element Container */}
                <div style={{ padding: '16px', backgroundColor: '#1C1C1C' }}>
                  <div style={{
                    padding: '14px 16px',
                    backgroundColor: '#0D0D0D',
                    border: '1px solid #333',
                    borderRadius: '4px',
                  }}>
                    <CardElement options={cardElementOptions} />
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Error */}
            {paymentError && (
              <div style={{
                padding: '12px 16px',
                backgroundColor: 'rgba(230, 50, 48, 0.1)',
                border: '1px solid #E63230',
                borderRadius: '4px',
                marginBottom: '16px',
                color: '#E63230',
                fontSize: '13px',
              }}>
                {paymentError}
              </div>
            )}

            {/* Pay Now Button */}
            <button
              type="submit"
              disabled={!stripe || isProcessing}
              style={{
                width: '100%',
                padding: '16px',
                backgroundColor: isProcessing ? '#333' : '#F1F1F1',
                color: isProcessing ? '#8A8A8A' : '#0D0D0D',
                border: 'none',
                borderRadius: '4px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: isProcessing ? 'not-allowed' : 'pointer',
                fontFamily: "'TASA Orbiter Display', sans-serif",
                transition: 'opacity 0.2s ease',
              }}
              onMouseEnter={(e) => !isProcessing && (e.target.style.opacity = '0.9')}
              onMouseLeave={(e) => e.target.style.opacity = '1'}
            >
              {isProcessing ? 'Processing...' : `Pay € ${orderTotal.toFixed(2)}`}
            </button>

            {/* Footer Links */}
            <div style={{
              marginTop: '32px',
              paddingTop: '24px',
              borderTop: '1px solid #1C1C1C',
              display: 'flex',
              justifyContent: 'center',
              gap: '24px',
            }}>
              <Link to="/privacy" style={{ color: '#6F6F6F', fontSize: '12px', textDecoration: 'none' }}>
                Privacy Policy
              </Link>
              <Link to="/returns" style={{ color: '#6F6F6F', fontSize: '12px', textDecoration: 'none' }}>
                Refund Policy
              </Link>
              <Link to="/shipping" style={{ color: '#6F6F6F', fontSize: '12px', textDecoration: 'none' }}>
                Shipping Policy
              </Link>
            </div>
          </form>
        </div>

        {/* Right Side - Order Summary */}
        <div className="checkout-order-section">
          {/* Cart Items */}
          <div style={{ marginBottom: '32px' }}>
            {cartItems.map((item, index) => (
              <div
                key={`${item.id}-${item.size}-${index}`}
                style={{
                  display: 'flex',
                  gap: '16px',
                  marginBottom: '16px',
                  paddingBottom: '16px',
                  borderBottom: index < cartItems.length - 1 ? '1px solid #1C1C1C' : 'none',
                }}
              >
                {/* Product Image with quantity badge */}
                <div style={{ position: 'relative' }}>
                  <div style={{
                    width: '64px',
                    height: '80px',
                    backgroundColor: '#1C1C1C',
                    borderRadius: '4px',
                    overflow: 'hidden',
                  }}>
                    <img
                      src={item.image}
                      alt={item.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  </div>
                  <span style={{
                    position: 'absolute',
                    top: '-8px',
                    right: '-8px',
                    backgroundColor: '#6F6F6F',
                    color: '#F1F1F1',
                    fontSize: '11px',
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    {item.quantity}
                  </span>
                </div>

                {/* Product Details */}
                <div style={{ flex: 1 }}>
                  <p style={{
                    color: '#F1F1F1',
                    fontSize: '14px',
                    marginBottom: '4px',
                  }}>
                    {item.name}
                  </p>
                  {item.size && (
                    <p style={{
                      color: '#8A8A8A',
                      fontSize: '13px',
                    }}>
                      Size: {item.size}
                    </p>
                  )}
                </div>

                {/* Price */}
                <p style={{
                  color: '#F1F1F1',
                  fontSize: '14px',
                }}>
                  € {item.price * item.quantity}
                </p>
              </div>
            ))}
          </div>

          {/* Order Totals */}
          <div style={{
            borderTop: '1px solid #1C1C1C',
            paddingTop: '24px',
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '12px',
            }}>
              <span style={{ color: '#8A8A8A', fontSize: '14px' }}>Subtotal</span>
              <span style={{ color: '#F1F1F1', fontSize: '14px' }}>€ {Number(cartTotal).toFixed(2)}</span>
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '16px',
            }}>
              <span style={{ color: '#8A8A8A', fontSize: '14px' }}>Shipping</span>
              <span style={{ color: '#F1F1F1', fontSize: '14px' }}>
                {shippingCost === 0 ? 'Free' : `€ ${shippingCost.toFixed(2)}`}
              </span>
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              paddingTop: '16px',
              borderTop: '1px solid #1C1C1C',
            }}>
              <span style={{ color: '#F1F1F1', fontSize: '16px' }}>Total</span>
              <div style={{ textAlign: 'right' }}>
                <span style={{ color: '#6F6F6F', fontSize: '12px', marginRight: '8px' }}>USD</span>
                <span style={{ color: '#F1F1F1', fontSize: '18px', fontWeight: '500' }}>€ {orderTotal.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

// Wrapper component that provides Stripe context
const Checkout = () => {
  return (
    <Elements stripe={stripePromise}>
      <CheckoutForm />
    </Elements>
  );
};

export default Checkout;